import * as React from 'react';
import MapPosition from '../Map/MapPosition';

import MapWithInfo from '~/components/MapWithInfo';
import { observer } from 'mobx-react';
import Alert from '~store/Alert/Alert';
import { useQuery } from '@tanstack/react-query';
import { AlertPriority, Device } from '@ixicare/resources';
import { useGPSGeofence, useLocation, useLocationRestrictedToAlarms } from '~hooks/location';
import { alertColors, formatDateTime, Spinner } from '@ixicare/ui';
import { parseDate } from '~utils/date';
import { t } from '~/i18n';
import LocationTimelineMap from '~containers/Map/LocationTimelineMap';
import { TimelineWindowInitial, useTimelineWindow } from '~components/LocationTimeline/useTimelineWindow';
import { TimelineBand, TimelineEvent } from '~components/LocationTimeline/timeline.utils';
import { alertUpdateEventText } from '~components/AlertLogs/AlertLogs';
import { ALERT_EVENT_COLORS, AlertActionEventKind, alertActionEventKind } from '~components/AlertLogs/AlertEventIcon';
import { hcpGroupHistoryText } from '~components/AlertSegmentationLogs/AlertSegmentationLogs';
import { useHcpGroups } from '~hooks/useHcpGroups';
import { alarmWindow, closedTime } from '~components/LocationTimeline/alarmWindows';

const HOUR = 60 * 60 * 1000;
const MAX_WINDOW = 24 * HOUR;
// Kept short, so the window reads as the alarm itself rather than an arbitrary zoom level.
const AROUND_ALARM = 5 * 60 * 1000;

interface AlertMapContainerProps {
  alert: Alert;
  parentAlert?: Alert;
}

const AlertMapContainer: React.FC<AlertMapContainerProps> = ({ alert, parentAlert }) => {
  if (!alert.device) return <ParentFallbackMap parentAlert={parentAlert} priority={alert.priority} />;
  // locationIsStatic only arrives with the alert detail, so opening an alarm from the list would
  // otherwise replay a route for a wall-mounted button until the detail lands.
  if (!alert.siteUuid) return <Spinner />;
  // A static device never moves, so there is no route to replay.
  if (!alert.device.locationIsStatic) return <AlertTimelineMap alert={alert} />;
  if (!alert.isOpen) return <OldLocationMap alert={alert} parentAlert={parentAlert} />;
  return <CurrentLocationMap alert={alert} parentAlert={parentAlert} />;
};

function actionEvent(kind: AlertActionEventKind) {
  return { kind, color: ALERT_EVENT_COLORS[kind] };
}

/** A closed alarm opens on its own window, from just before it triggered until just after it closed; an open one follows live. */
function initialAlertWindow(alert: Alert): TimelineWindowInitial {
  const closed = closedTime(alert);
  if (closed === null) return { live: true, span: HOUR };
  const start = alert.created.getTime() - AROUND_ALARM;
  const end = Math.min(Date.now(), closed + AROUND_ALARM, start + MAX_WINDOW);
  return { live: false, start, end, pinned: alert.created.getTime() };
}

const AlertTimelineMap = observer(({ alert }: { alert: Alert }) => {
  const timeline = useTimelineWindow(initialAlertWindow(alert));
  const restricted = useLocationRestrictedToAlarms(alert.siteUuid);
  const geofence = useGPSGeofence(alert.resident?.id);
  const { data: hcpGroups } = useHcpGroups(alert.siteUuid ?? undefined, { staleTime: 5 * 60 * 1000 });
  const color = alertColors[alert.priority];

  const events: TimelineEvent[] = [
    {
      time: alert.created.getTime(),
      kind: 'triggered',
      color,
      text: t('alert.logs.types.create', { datetime: formatDateTime(alert.created) }),
    },
    ...alert.alertUpdateEvents.map((event) => ({
      time: event.created.getTime(),
      ...actionEvent(alertActionEventKind(event.action)),
      text: alertUpdateEventText(event, hcpGroups),
    })),
    ...alert.hcpGroupHistory.map((entry) => ({
      time: entry.createdAt.getTime(),
      ...actionEvent('escalated'),
      text: hcpGroupHistoryText(entry, hcpGroups),
    })),
  ];
  if (alert.isCancelled && alert.cancelledTime) {
    events.push({
      time: alert.cancelledTime.getTime(),
      ...actionEvent('cancelled'),
      text: t('alert.logs.types.cancel', { datetime: formatDateTime(alert.cancelledTime) }),
    });
  }
  const bands: TimelineBand[] = [{ start: alert.created.getTime(), end: closedTime(alert) ?? timeline.now, color }];

  if (restricted === undefined) return <Spinner />;
  return (
    <LocationTimelineMap
      deviceId={alert.device!.id}
      timeline={timeline}
      siteUuid={alert.siteUuid ?? undefined}
      type={alert.priority}
      geofence={geofence}
      events={events}
      bands={bands}
      allowLive={!alert.isResolved}
      initialLabel={alert.isResolved ? t('locationTimeline.alarmWindow') : undefined}
      visibleWindows={restricted ? [alarmWindow(alert)] : undefined}
    />
  );
});

function ParentFallbackMap({ parentAlert, priority }: { parentAlert?: Alert; priority: AlertPriority }) {
  const restricted = useLocationRestrictedToAlarms(parentAlert?.siteUuid);
  if (!parentAlert?.device?.location?.lastKnown) return null;
  // The last known position is where the device is now, which only an alarm still running may show.
  if (restricted !== false && !parentAlert.device.locationIsStatic && parentAlert.isResolved) return null;
  const lastKnown = parentAlert.device.location.lastKnown;
  const label = t('alert.backup.locationOf', { name: parentAlert.description });
  const info = lastKnown.timestamp ? `${label} (${formatDateTime(parseDate(lastKnown.timestamp))})` : label;
  return (
    <MapWithInfo info={info}>
      <MapPosition locationData={lastKnown} isStatic={parentAlert.device.locationIsStatic ?? false} type={priority} />
    </MapWithInfo>
  );
}

function OldLocationMap({ alert, parentAlert }: AlertMapContainerProps) {
  const datetime = alert.acceptedTime ?? alert.resolvedTime ?? alert.cancelledTime ?? null; // Auto-resolve by backend won't produce accept event
  const deviceId = alert.device?.uuid ?? -1;
  let location = alert.device?.location;
  let locationTimestamp: Date | null = alert.created;
  if (!(alert.device?.locationIsStatic ?? false)) {
    location = useQuery({
      queryKey: ['device-location', deviceId, datetime?.toDateString()],
      queryFn: () => (datetime ? Device.getDeviceLocation(deviceId, datetime) : Device.getDeviceLastLocation(deviceId)),
      initialData: alert.device?.location ?? undefined,
    }).data;
    if (location?.lastKnown?.timestamp) {
      locationTimestamp = parseDate(location?.lastKnown?.timestamp);
    }
  }

  if (!location?.lastKnown) {
    return <ParentFallbackMap parentAlert={parentAlert} priority={alert.priority} />;
  }

  let info = formatDateTime(locationTimestamp);
  if (location?.lastKnown?.description) {
    info = location?.lastKnown?.description + ` (${info})`;
  }
  return (
    <MapWithInfo info={info}>
      <MapPosition
        locationData={location?.lastKnown ?? undefined}
        isStatic={location?.isStatic ?? false}
        type={alert.priority}
      />
    </MapWithInfo>
  );
}

function CurrentLocationMap({ alert, parentAlert }: AlertMapContainerProps) {
  const geo = useGPSGeofence(alert.resident?.id);

  const locationData = useLocation(
    alert.device?.id,
    alert.device?.location?.lastKnown ?? undefined,
    alert.device?.locationIsStatic ?? false
  );

  if (!locationData) {
    return <ParentFallbackMap parentAlert={parentAlert} priority={alert.priority} />;
  }

  const info =
    (locationData?.description ?? '') +
    (locationData?.timestamp ? ` (${formatDateTime(parseDate(locationData?.timestamp))})` : '');
  return !alert.siteUuid ? (
    <Spinner />
  ) : (
    <MapWithInfo info={info}>
      <MapPosition
        locationData={locationData}
        siteUuid={alert.siteUuid}
        isStatic={alert.device?.locationIsStatic ?? false}
        type={alert.priority}
        geofence={geo}
      />
    </MapWithInfo>
  );
}

export default observer(AlertMapContainer);
